/**
 * Per-submenu-item count loaders for the Approvals navbar badge.
 *
 * Each loader is a dynamic import so the navbar bundle does not pull in those
 * feature modules (or their pages) just to show a number. Add a loader keyed
 * by the same `to` as the item in PROCUREMENT_NAV_ITEMS (`countsAwaiting`) when a new queue appears.
 */

export type ApprovalsCountByPath = Record<string, number>;

/** The Equipment nav item; its badge is the open Critical defect count (EQ-30, NAV-09). */
export const EQUIPMENT_NAV_PATH = '/equipment';

const APPROVALS_COUNT_LOADERS: Record<string, () => Promise<number>> = {
  '/vendor-approvals': loadVendorAdminCount,
  // Not an approvals queue: NavBar asks for this path on its own, for the Equipment item only.
  [EQUIPMENT_NAV_PATH]: loadCriticalDefectsCount,
};

/**
 * Fired after anything that changes an approval queue (approve, reject, withdraw, activate),
 * so the navbar badge refetches immediately instead of on the next navigation (M3).
 */
export const APPROVALS_CHANGED_EVENT = 'wrap:approvals-changed';

export function notifyApprovalsChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(APPROVALS_CHANGED_EVENT));
}

/**
 * Call after adding, resolving or signing off a defect so the Equipment badge (and the Critical
 * Defects widget, if mounted) refetch straight away. Shares the approvals event, so every nav
 * count refreshes; they are cheap.
 */
export function notifyCriticalDefectsChanged(): void {
  notifyApprovalsChanged();
}

async function loadVendorAdminCount(): Promise<number> {
  const { vendorService } = await import('../services/vendorService');
  const summary = await vendorService.getPendingSummary();
  return summary.total;
}

/** Open Critical defects on units the caller can see (same query as the Home widget). */
async function loadCriticalDefectsCount(): Promise<number> {
  const { vehicleDefectsService } = await import('../services/vehicleDefectsService');
  return vehicleDefectsService.criticalCount();
}

export function formatNavCount(count: number): string {
  if (count > 99) return '99+';
  return String(count);
}

export async function loadAwaitingApprovalsCounts(paths: string[]): Promise<ApprovalsCountByPath> {
  const unique = [...new Set(paths.filter((path) => APPROVALS_COUNT_LOADERS[path]))];
  const entries = await Promise.all(
    unique.map(async (path) => {
      try {
        const count = await APPROVALS_COUNT_LOADERS[path]();
        return [path, Number.isFinite(count) ? count : 0] as const;
      } catch {
        return [path, 0] as const;
      }
    })
  );
  return Object.fromEntries(entries);
}
